import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { CustomModal } from "@/components/Modal";
import { Button, ButtonVariantOption } from "@/components/Button";
import { PageTitle } from "@/components/Title";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { CTextField } from "@/components/TextField/CTextField";
import type { EditNameModalProps } from "@/features/dmManagement/types";

const editNameSchema = z.object({
  name: z
    .string()
    .min(1, "識別名を入力してください")
    .max(256, "256文字以内で入力してください"),
});

type EditNameFormValues = z.infer<typeof editNameSchema>;

/**
 * 識別名編集モーダル
 * デザインや注文の識別名をインラインで編集・保存するためのモーダルダイアログ
 */
export const EditNameModal: React.FC<EditNameModalProps> = ({
  open,
  onClose,
  currentName,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<EditNameFormValues>({
    mode: "onChange",
    defaultValues: {
      name: currentName,
    },
    resolver: zodResolver(editNameSchema),
  });

  const onSubmit = async (data: EditNameFormValues): Promise<void> => {
    setIsSaving(true);
    try {
      await onSave(data.name);
      onClose();
      toast.success("識別名の更新が完了しました。");
    } catch {
      toast.error("識別名の更新に失敗しました。");
    } finally {
      setIsSaving(false);
    }
  };

  const handleModalClose = (
    _event: Record<string, unknown>,
    _reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    onClose();
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleModalClose}
      ariaLabelledby="edit-name-modal"
      ariaDescribedby="edit-name-modal-description"
      width={"500px"}
      maxHeight={"90vh"}
      overflow="auto"
    >
      <Box
        component="form"
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
      >
        <Stack direction={"column"} spacing={3}>
          <PageTitle>識別名の編集</PageTitle>
          <CTextField
            control={control}
            name="name"
            label="識別名"
            autoFocus
            size={SizingWrapperStyle.INHERIT}
          />
          <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
            <Button
              label="キャンセル"
              onClick={onClose}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Outlined}
            />
            <Button
              label="保存"
              onClick={() => {
                void handleSubmit(onSubmit)();
              }}
              disabled={!isValid || isSaving}
              size={SizingWrapperStyle.INHERIT}
              variant={ButtonVariantOption.Contained}
              hasLoading={true}
              loading={isSaving}
            />
          </Stack>
        </Stack>
      </Box>
    </CustomModal>
  );
};
